import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import {
    AddFreeService,
    NeedAddTrialActionType,
    type AddFreeService as AddFreeServiceType,
} from '@telecom/shared/domain/permission';
import {
    CreditsChargeService,
    type CreditsChargeService as CreditsChargeServiceType,
} from '../../credits-charge-service/credits-charge-service';
import { CREDITS_CHARGE_TOAST_DURATION_MS } from '../../credits-charge-service/impls/credits-charge-service';
import type {
    NewUserAccessAction,
    NewUserInitializationService,
} from '../new-user-initialization-service';
import { isUserLogin } from '@telecom/shared/startup/user-auth';

/** Credits Charge Toast 离场动画时长。 */
const CREDITS_CHARGE_TOAST_LEAVE_DURATION_MS = 200;
/** 领取成功到首次引导可启动之间的完整等待时长。 */
const CREDITS_CHARGE_TOAST_COMPLETE_DURATION_MS = CREDITS_CHARGE_TOAST_DURATION_MS
    + CREDITS_CHARGE_TOAST_LEAVE_DURATION_MS;

/** 新用户赋权、积分领取与首次引导门禁的应用级编排器。 */
@injectable()
export class NewUserInitializationServiceImpl implements NewUserInitializationService {
    /** 当前应用生命周期唯一的产品准入 Promise。 */
    private _prepareAccessPromise?: Promise<NewUserAccessAction>;
    /** 当前应用生命周期唯一的挂载后初始化 Promise。 */
    private _completeAfterAppMountPromise?: Promise<void>;
    /** 产品准入检查是否正在执行。 */
    private _isPreparingAccess = false;
    /** 首次引导共享等待结果。 */
    private readonly _entryOnboardingPromise: Promise<boolean>;
    /** 完成首次引导共享等待结果。 */
    private _resolveEntryOnboarding!: (canContinue: boolean) => void;
    /** 防止重复完成首次引导共享等待结果。 */
    private _isEntryOnboardingSettled = false;

    constructor(
        /** 执行产品准入检查并分发赋权、跳转或直接进入动作。 */
        @inject(AddFreeService)
        private readonly _addFreeService: AddFreeServiceType,
        /** 执行登录、区域和积分资格检查。 */
        @inject(CreditsChargeService)
        private readonly _creditsChargeService: CreditsChargeServiceType,
    ) {
        this._entryOnboardingPromise = new Promise<boolean>((resolve) => {
            this._resolveEntryOnboarding = resolve;
        });
    }

    /** 幂等执行挂载前产品准入检查。 */
    prepareAccess(): Promise<NewUserAccessAction> {
        if (!this._prepareAccessPromise) {
            this._prepareAccessPromise = this._prepareAccess();
        }
        return this._prepareAccessPromise;
    }

    /** 幂等执行挂载后积分检查和 Toast 退场等待。 */
    async completeAfterAppMount(): Promise<void> {
        if (!await isUserLogin()) {
            return;
        }
        if (!this._completeAfterAppMountPromise) {
            this._completeAfterAppMountPromise = this._completeAfterAppMount();
        }
        return this._completeAfterAppMountPromise;
    }

    /** 返回首次引导共享等待结果。 */
    waitForEntryOnboarding(): Promise<boolean> {
        return this._entryOnboardingPromise;
    }

    /** 返回产品准入检查当前是否仍在执行。 */
    isPreparingAccess(): boolean {
        return this._isPreparingAccess;
    }

    /** 执行准入检查，并将异常收敛为当前页面无法继续。 */
    private async _prepareAccess(): Promise<NewUserAccessAction> {
        this._isPreparingAccess = true;
        try {
            if (!this._addFreeService.isAutoAddTrialEnabled()) return undefined;
            return await this._addFreeService.addFreePkg([]);
        }
        catch (error) {
            console.warn('Permission initialization failed:', error);
            return undefined;
        }
        finally {
            this._isPreparingAccess = false;
        }
    }

    /** 仅在当前页面产品准入完成后检查积分并释放首次引导。 */
    private async _completeAfterAppMount(): Promise<void> {
        const actionType = await this.prepareAccess();
        if (actionType !== NeedAddTrialActionType.DIRECT_LOGIN) {
            this._settleEntryOnboarding(false);
            return;
        }

        try {
            const result = await this._creditsChargeService.checkAndClaim();
            if (result.status === 'claimed') {
                await this._waitForCreditsToast();
            }
        }
        catch (error) {
            console.warn('Credits charge initialization failed:', error);
        }
        this._settleEntryOnboarding(true);
    }

    /** 等待领取成功 Toast 展示及离场完成。 */
    private _waitForCreditsToast(): Promise<void> {
        return new Promise<void>((resolve) => {
            setTimeout(resolve, CREDITS_CHARGE_TOAST_COMPLETE_DURATION_MS);
        });
    }

    /** 幂等完成首次引导共享等待结果。 */
    private _settleEntryOnboarding(canContinue: boolean): void {
        if (this._isEntryOnboardingSettled) return;
        this._isEntryOnboardingSettled = true;
        this._resolveEntryOnboarding(canContinue);
    }
}
